import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Star, Zap, Heart, Check, X } from "lucide-react";

interface Match {
  user_id: string;
  display_name: string | null;
  photo_urls: string[] | null;
  common_count: number;
  match_tier: string;
  bio?: string;
}

interface MatchHorizontalCardProps {
  match: Match;
  onApprove: () => void;
  onReject: () => void;
}

const MatchHorizontalCard = ({ match, onApprove, onReject }: MatchHorizontalCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getMatchTierIcon = (tier: string) => {
    switch (tier) {
      case 'gold': return <Star className="w-4 h-4 text-neon-red" />;
      case 'silver': return <Zap className="w-4 h-4 text-toxic-green" />;
      case 'bronze': return <Heart className="w-4 h-4 text-electric-violet" />;
      default: return <Heart className="w-4 h-4 text-ice-white/60" />;
    }
  };

  const getMatchTierGlow = (tier: string) => {
    switch (tier) {
      case 'gold': return 'shadow-neon-red border-neon-red/30';
      case 'silver': return 'shadow-toxic-green border-toxic-green/30';
      case 'bronze': return 'shadow-electric-violet border-electric-violet/30';
      default: return 'border-ice-white/20';
    }
  };

  return (
    <div className={`w-80 h-[500px] dark-pod overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 ${getMatchTierGlow(match.match_tier)}`}>
      {/* Main Profile Photo */}
      <div className="relative h-3/5 bg-carbon">
        {match.photo_urls && match.photo_urls.length > 0 ? (
          <img
            src={match.photo_urls[0]}
            alt={match.display_name || 'Profile'}
            className="w-full h-full object-cover"
            onClick={() => setIsExpanded(!isExpanded)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-16 h-16 text-ice-white/40" />
          </div>
        )}

        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-transparent" />

        {/* Match tier indicator */}
        <div className="absolute top-4 right-4 flex items-center gap-1">
          {getMatchTierIcon(match.match_tier)}
          <Badge variant="outline" className="bg-carbon/80 border-ice-white/20 text-ice-white text-xs">
            {match.match_tier}
          </Badge>
        </div>

        {/* Profile Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-ice-white">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold neon-text">
              {match.display_name || 'Anonymous'}
            </h3>
            <Badge variant="secondary" className="bg-ice-white/20 text-ice-white border-ice-white/30 text-xs">
              {match.common_count} shared
            </Badge>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="h-2/5 p-4 flex flex-col justify-between">
        {/* Bio Section */}
        <div className="flex-1">
          {match.bio && (
            <p className={`text-sm text-ice-white/80 leading-relaxed ${
              isExpanded ? '' : 'line-clamp-3'
            }`}>
              {match.bio}
            </p>
          )}
          {match.bio && match.bio.length > 100 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-electric-violet hover:text-electric-violet/80 mt-2"
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <Button
            onClick={onReject}
            variant="outline"
            size="sm"
            className="flex-1 border-neon-red/30 text-neon-red hover:bg-neon-red/10"
          >
            <X className="w-4 h-4 mr-2" />
            Pass
          </Button>
          <Button
            onClick={onApprove}
            size="sm"
            className="flex-1 bg-toxic-green hover:bg-toxic-green/80 text-carbon"
          >
            <Check className="w-4 h-4 mr-2" />
            Match
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MatchHorizontalCard;